import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { Download, Search, Trash2, Eye, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useUserContext } from '@/hooks/useUserContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RiskRatingBadge } from '@/components/RiskRatingBadge';
import { SiteFilter } from '@/components/admin/SiteFilter';
import { ScoreBreakdownDisplay } from '@/components/admin/ScoreBreakdownDisplay';
import { useSubscriptionFeatures, useUserCompanyId } from '@/hooks/useSubscriptionFeatures';
import { useNavigate } from 'react-router-dom';

interface Report {
  id: string;
  created_at: string;
  hazard_classification: string;
  hazard_category: string;
  location: string;
  description: string;
  severity: number;
  probability: number;
  risk_score: number;
  risk_rating: string;
  verification_status: string;
  reporter_id: string;
  reported_worker_id: string | null;
  supervisor_id: string | null;
  photo_url: string | null;
  evidence_score: number | null;
  truth_score: number | null;
  supervisor_notes: string | null;
  vision_confidence: number | null;
  audio_confidence: number | null;
  consistency_score: number | null;
  geo_shift_match: number | null;
  peer_corroboration_score: number | null;
  tamper_penalty: number | null;
  photo_analysis_data: any;
  site_id: string | null;
  // Additional raw data fields
  work_activity: string | null;
  cause_category: string | null;
  cause_details: string | null;
  gps_coordinates: string | null;
  gps_location: string | null;
  audio_input_method: string | null;
  confidence_score: number | null;
  clarification_response: string | null;
  clarification_responded_at: string | null;
  xp_awarded: number | null;
  xp_pending: boolean | null;
  score_breakdown: any | null;
  // Joined data
  reporter_name?: string;
  reported_worker_name?: string;
}

interface AdminReportsProps {
  siteId?: string | null;
}

const AdminReports = ({ siteId: propSiteId }: AdminReportsProps = {}) => {
  const { companyId, isLoading: contextLoading } = useUserContext();
  const { companyId: subCompanyId } = useUserCompanyId();
  const { hasFeature } = useSubscriptionFeatures(subCompanyId || undefined);
  const navigate = useNavigate();
  
  const canExport = hasFeature('dataExport');
  
  const [reports, setReports] = useState<Report[]>([]);
  const [filteredReports, setFilteredReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState(propSiteId || 'all');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  // Use prop siteId if provided
  useEffect(() => {
    if (propSiteId) {
      setSiteFilter(propSiteId);
    }
  }, [propSiteId]);

  useEffect(() => {
    if (!contextLoading && companyId) {
      fetchReports();
    }
  }, [contextLoading, companyId]);

  useEffect(() => {
    filterReports();
  }, [reports, searchTerm, statusFilter, riskFilter, siteFilter]);

  const fetchReports = async () => {
    if (!companyId) return;
    
    try {
      // First get all site IDs for this company
      const { data: companySites } = await supabase
        .from('company_locations')
        .select('id')
        .eq('company_id', companyId);
      
      const siteIds = (companySites || []).map(s => s.id);
      
      // Build query - filter by site_ids belonging to this company
      let query = (supabase as any)
        .from('peer_reports')
        .select(`
          *,
          reporter:worker_profiles!reporter_id(display_name, full_name),
          reported_worker:worker_profiles!reported_worker_id(display_name, full_name)
        `)
        .order('created_at', { ascending: false });
      
      // Filter by sites belonging to this company
      if (siteIds.length > 0) {
        query = query.in('site_id', siteIds);
      } else {
        // If no sites found, return empty (no reports to show)
        setReports([]);
        setLoading(false);
        return;
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Transform data with reporter names
      const transformedData: Report[] = (data || []).map((r: any) => ({
        ...r,
        reporter_name: r.reporter?.display_name || r.reporter?.full_name || null,
        reported_worker_name: r.reported_worker?.display_name || r.reported_worker?.full_name || null,
      }));
      
      setReports(transformedData);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const filterReports = () => {
    let filtered = [...reports];

    if (searchTerm) {
      filtered = filtered.filter(
        (r) =>
          r.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.hazard_category.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (r.reporter_name && r.reporter_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.verification_status === statusFilter);
    }

    if (riskFilter !== 'all') {
      filtered = filtered.filter((r) => r.risk_rating === riskFilter);
    }

    if (siteFilter !== 'all') {
      filtered = filtered.filter((r) => r.site_id === siteFilter);
    }

    setFilteredReports(filtered);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this report?')) return;

    try {
      const { error } = await supabase.from('peer_reports').delete().eq('id', id);
      if (error) throw error;
      
      toast.success('Report deleted successfully');
      fetchReports();
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report');
    }
  };

  const exportToCSV = () => {
    // Check if user has export feature
    if (!canExport) {
      toast.error('Export requires Professional plan or higher', {
        action: { label: 'Upgrade', onClick: () => navigate('/admin?tab=settings') }
      });
      return;
    }

    const headers = [
      'ID',
      'Date',
      'Reporter',
      'Reported Worker',
      'Classification',
      'Category',
      'Location',
      'Description',
      'Work Activity',
      'Cause Category',
      'Cause Details',
      'Severity',
      'Probability',
      'Risk Score',
      'Risk Rating',
      'Status',
      'Evidence Score',
      'Truth Score',
      'GPS Coordinates',
      'GPS Location',
      'Audio Input Method',
      'Confidence Score',
      'Vision Confidence',
      'Audio Confidence',
      'Consistency Score',
      'Geo Match',
      'Peer Corroboration',
      'Tamper Penalty',
      'XP Awarded',
      'XP Pending',
      'Clarification Response',
      'Clarification Responded At',
      'Supervisor Notes',
      'Photo URL',
    ];

    const csvData = filteredReports.map((r) => [
      r.id,
      new Date(r.created_at).toLocaleString(),
      r.reporter_name || r.reporter_id,
      r.reported_worker_name || r.reported_worker_id || 'N/A',
      r.hazard_classification,
      r.hazard_category,
      r.location,
      r.description,
      r.work_activity || 'N/A',
      r.cause_category || 'N/A',
      r.cause_details || 'N/A',
      r.severity,
      r.probability,
      r.risk_score,
      r.risk_rating,
      r.verification_status,
      r.evidence_score ?? 'N/A',
      r.truth_score !== null ? `${Math.round(r.truth_score * 100)}%` : 'N/A',
      r.gps_coordinates || 'N/A',
      r.gps_location || 'N/A',
      r.audio_input_method || 'N/A',
      r.confidence_score !== null ? `${Math.round(r.confidence_score * 100)}%` : 'N/A',
      r.vision_confidence !== null ? `${Math.round(r.vision_confidence * 100)}%` : 'N/A',
      r.audio_confidence !== null ? `${Math.round(r.audio_confidence * 100)}%` : 'N/A',
      r.consistency_score !== null ? `${Math.round(r.consistency_score * 100)}%` : 'N/A',
      r.geo_shift_match !== null ? `${Math.round(r.geo_shift_match * 100)}%` : 'N/A',
      r.peer_corroboration_score !== null ? `${Math.round(r.peer_corroboration_score * 100)}%` : 'N/A',
      r.tamper_penalty ?? 'N/A',
      r.xp_awarded ?? 0,
      r.xp_pending ? 'Yes' : 'No',
      r.clarification_response || 'N/A',
      r.clarification_responded_at ? new Date(r.clarification_responded_at).toLocaleString() : 'N/A',
      r.supervisor_notes || 'N/A',
      r.photo_url || 'N/A',
    ]);

    const csvContent = [headers, ...csvData].map((row) => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sorin-reports-full-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
      hazard_confirmed: { variant: 'default', label: 'Confirmed' },
      verified: { variant: 'default', label: 'Verified' },
      peer_corroboration_requested: { variant: 'outline', label: 'Peer Corroboration' },
      pending_verification: { variant: 'secondary', label: 'Pending' },
      rejected: { variant: 'destructive', label: 'Rejected' },
    };
    const config = configs[status] || { variant: 'secondary', label: status.replace(/_/g, ' ') };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getEvidenceScoreDisplay = (report: Report) => {
    return (
      <ScoreBreakdownDisplay 
        score={report.evidence_score} 
        breakdown={report.score_breakdown} 
        compact={true} 
      />
    );
  };

  const handleViewReport = (report: Report) => {
    setSelectedReport(report);
    setShowDetail(true);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>All Hazard Reports (Raw Data)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by location, description, category, or reporter..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {!propSiteId && (
              <SiteFilter 
                value={siteFilter}
                onChange={setSiteFilter}
                className="w-full md:w-48"
              />
            )}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending_verification">Pending Verification</SelectItem>
                <SelectItem value="peer_corroboration_requested">Peer Corroboration</SelectItem>
                <SelectItem value="hazard_confirmed">Hazard Confirmed</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by risk" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk Levels</SelectItem>
                <SelectItem value="very_low">Very Low</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="very_high">Very High</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              onClick={exportToCSV} 
              variant="outline"
              className={!canExport ? "opacity-70" : ""}
            >
              {canExport ? (
                <Download className="h-4 w-4 mr-2" />
              ) : (
                <Lock className="h-4 w-4 mr-2" />
              )}
              Export CSV
            </Button>
          </div>

          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Reporter</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Work Activity</TableHead>
                  <TableHead>Cause</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Quality Score</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8">
                      Loading reports...
                    </TableCell>
                  </TableRow>
                ) : filteredReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                      No reports found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell className="whitespace-nowrap">
                        {new Date(report.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="max-w-[120px] truncate">
                        {report.reporter_name || 'Unknown'}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{report.description}</TableCell>
                      <TableCell className="capitalize whitespace-nowrap">
                        {report.hazard_category.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell className="max-w-[100px] truncate capitalize">
                        {report.work_activity?.replace(/_/g, ' ') || '-'}
                      </TableCell>
                      <TableCell className="max-w-[100px] truncate capitalize">
                        {report.cause_category?.replace(/_/g, ' ') || '-'}
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">{report.location}</TableCell>
                      <TableCell>
                        <RiskRatingBadge rating={report.risk_rating as any} score={report.risk_score} showScore={false} />
                      </TableCell>
                      <TableCell>{getStatusBadge(report.verification_status)}</TableCell>
                      <TableCell>
                        {getEvidenceScoreDisplay(report)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewReport(report)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(report.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Report Details (Raw Data)</DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">Date & Time</label>
                  <p className="text-sm text-muted-foreground">
                    {new Date(selectedReport.created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedReport.verification_status)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium">Report ID</label>
                  <p className="text-xs text-muted-foreground font-mono">{selectedReport.id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Reporter</label>
                  <p className="text-sm text-muted-foreground">
                    {selectedReport.reporter_name || 'Unknown'}
                  </p>
                  <p className="text-xs text-muted-foreground font-mono">{selectedReport.reporter_id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Reported Worker</label>
                  <p className="text-sm text-muted-foreground">
                    {selectedReport.reported_worker_name || (selectedReport.reported_worker_id ? 'ID: ' + selectedReport.reported_worker_id.slice(0, 8) : 'N/A')}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Supervisor</label>
                  <p className="text-sm text-muted-foreground">
                    {selectedReport.supervisor_id ? selectedReport.supervisor_id.slice(0, 8) : 'Not assigned'}
                  </p>
                </div>
              </div>

              {/* Classification */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium">Classification</label>
                  <p className="text-sm text-muted-foreground capitalize">
                    {selectedReport.hazard_classification.replace(/_/g, ' ')}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Category</label>
                  <p className="text-sm text-muted-foreground capitalize">
                    {selectedReport.hazard_category.replace(/_/g, ' ')}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Work Activity</label>
                  <p className="text-sm text-muted-foreground capitalize">
                    {selectedReport.work_activity?.replace(/_/g, ' ') || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Location</label>
                  <p className="text-sm text-muted-foreground">{selectedReport.location}</p>
                </div>
              </div>

              {/* Cause Analysis */}
              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-sm">Cause Analysis</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground">Cause Category</label>
                    <p className="text-sm capitalize">{selectedReport.cause_category?.replace(/_/g, ' ') || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Cause Details</label>
                    <p className="text-sm">{selectedReport.cause_details || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Risk Assessment */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium">Severity</label>
                  <p className="text-sm text-muted-foreground">{selectedReport.severity}/5</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Probability</label>
                  <p className="text-sm text-muted-foreground">{selectedReport.probability}/5</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Risk Score</label>
                  <p className="text-sm text-muted-foreground">{selectedReport.risk_score}/25</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Risk Rating</label>
                  <div className="mt-1">
                    <RiskRatingBadge rating={selectedReport.risk_rating as any} score={selectedReport.risk_score} />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Description</label>
                <p className="text-sm text-muted-foreground mt-1">{selectedReport.description}</p>
              </div>

              {/* Location Data */}
              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-sm">Location Data</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-xs text-muted-foreground">GPS Coordinates</label>
                    <p className="font-mono text-xs">{selectedReport.gps_coordinates || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">GPS Location</label>
                    <p>{selectedReport.gps_location || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Input Method */}
              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-sm">Input Method</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-xs text-muted-foreground">Audio Input Method</label>
                    <p>{selectedReport.audio_input_method || 'Text'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Confidence Score</label>
                    <p>{selectedReport.confidence_score !== null ? `${Math.round(selectedReport.confidence_score * 100)}%` : 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Report Quality Score with Full Breakdown */}
              <div className="border rounded-lg p-4 space-y-3">
                <ScoreBreakdownDisplay 
                  score={selectedReport.evidence_score} 
                  breakdown={selectedReport.score_breakdown} 
                  compact={false} 
                />
              </div>

              {/* XP Info */}
              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-sm">XP Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-xs text-muted-foreground">XP Awarded</label>
                    <p className="font-medium">{selectedReport.xp_awarded ?? 0}</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">XP Pending</label>
                    <p className="font-medium">{selectedReport.xp_pending ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>

              {/* Clarification */}
              {(selectedReport.clarification_response || selectedReport.clarification_responded_at) && (
                <div className="border rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-sm">Clarification Response</h3>
                  <p className="text-sm text-muted-foreground">{selectedReport.clarification_response || 'N/A'}</p>
                  {selectedReport.clarification_responded_at && (
                    <p className="text-xs text-muted-foreground">
                      Responded at: {new Date(selectedReport.clarification_responded_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              {selectedReport.supervisor_notes && (
                <div>
                  <label className="text-sm font-medium">Supervisor Notes</label>
                  <p className="text-sm text-muted-foreground mt-1 p-3 bg-muted rounded-lg">
                    {selectedReport.supervisor_notes}
                  </p>
                </div>
              )}
              
              {selectedReport.photo_url && (
                <div>
                  <label className="text-sm font-medium">Photo Evidence</label>
                  <img
                    src={selectedReport.photo_url}
                    alt="Report evidence"
                    className="mt-2 rounded-lg max-w-full h-auto"
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReports;
